<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
  store.dispatch('agents/get');
});

// Re-sync contactData when the store updates selectedContact
watch(() => props.selectedContact, (newVal) => {
  if (newVal) {
    Object.assign(contactData.value, { ...newVal });
  }
}, { deep: true });

// Owner dropdown (MultiselectDropdown)
const agents = computed(() => store.getters['agents/getAgents'] || []);
const currentUser = computed(() => store.getters['getCurrentUser'] || {});
const { accountId } = useAccount();
const isAdmin = computed(() => {
  const perms = getUserPermissions(currentUser.value, accountId.value);
  return perms.includes('administrator');
});
const canChangeOwner = computed(() => {
  if (isAdmin.value) return true;
  const ownerId = props.selectedContact?.userId;
  return !ownerId || ownerId === currentUser.value.id;
});

const ownerDropdownOptions = computed(() => {
  const none = { id: null, name: 'Sem responsável' };
  return [none, ...agents.value.map(a => ({
    id: a.id,
    name: a.name,
    thumbnail: a.avatar_url || a.thumbnail,
  }))];
});

const selectedOwnerItem = computed(() => {
  const userId = props.selectedContact?.userId;
  if (!userId) return { id: null, name: 'Sem responsável' };
  const agent = agents.value.find(a => a.id === userId);
  return agent
    ? { id: agent.id, name: agent.name, thumbnail: agent.avatar_url || agent.thumbnail }
    : { id: null, name: 'Sem responsável' };
});

const onSelectOwner = async (item) => {
  if (!canChangeOwner.value) return;
  const userId = item.id || null;
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      user_id: userId,
    });
    contactData.value.userId = userId;
    useAlert('Responsável atualizado.');
  } catch (error) {
    useAlert('Erro ao atualizar responsável.');
  }
};

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar :src="avatarSrc || ''" :name="selectedContact?.name || ''" :size="72" allow-upload
        @upload="handleAvatarUpload" @delete="handleAvatarDelete" />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span v-if="selectedContact?.identifier" class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span v-if="selectedContact?.identifier" class="i-ph-activity text-n-slate-10 size-4" />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
    </div>
    <div class="w-full">
      <div class="flex items-center gap-2 px-1 mb-1">
        <span class="i-lucide-briefcase size-3.5" style="color: #8b5cf6;" />
        <span class="text-xs font-medium text-n-slate-10">Responsável</span>
      </div>
      <MultiselectDropdown :options="ownerDropdownOptions" :selected-item="selectedOwnerItem"
        multiselector-title="Responsável" multiselector-placeholder="Nenhum responsável"
        no-search-result="Nenhum agente encontrado" input-placeholder="Buscar agente..." :disabled="!canChangeOwner"
        @select="onSelectOwner" />
      <p v-if="!canChangeOwner" class="text-xs text-n-slate-9 mt-1 px-1">
        Apenas o responsável atual ou administradores podem alterar.
      </p>
    </div>
    <div class="flex flex-col items-start gap-6">
      <ContactsForm ref="contactsFormRef" :contact-data="contactData" is-details-view @update="handleFormUpdate" />
      <Button :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')" size="sm" :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid" @click="updateContact" />
    </div>
    <Policy :permissions="['administrator']">
      <div class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong">
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')" color="ruby"
          @click="openConfirmDeleteContactDialog" />
      </div>
      <ConfirmContactDeleteDialog ref="confirmDeleteContactDialogRef" :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')" />
    </Policy>
  </div>
</template>
